import { ActionIcon, Menu, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { Icon, toastNotifications } from '@uag/react-core';
import { MouseEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiResponseOfBasicTenantModel } from 'api/v3/models';
import { useResetTenant, getGetTenantsQueryKey } from 'api/v3/tenant/tenant';
import { getSearchUsersQueryKey } from 'api/v3/user/user';
import { usePermissions } from 'modules/shared/permissions';
import { ConfirmationModal } from 'shared/components/confirmationModal/ConfirmationModal';
import { RowActionVariant } from 'shared/components/extendedDataTable';

export const ResetTenantAction = (tenant: ApiResponseOfBasicTenantModel, _index: number, variant: RowActionVariant) => {
    const { t } = useTranslation();
    const canReset = usePermissions('ResetTenant', 'Tenant', tenant);

    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const id = useId();

    const queryClient = useQueryClient();
    const { mutateAsync: resetTenant } = useResetTenant();

    if (!canReset) {
        return null;
    }

    const handleResetTenantClick = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setOpen(true);
    };

    const handleConfirm = async (event?: MouseEvent) => {
        event?.stopPropagation?.();
        setOpen(false);

        toastNotifications.loading({
            id,
            message: t('willBe', {
                type: t('tenant'),
                name: tenant.data.name,
                action: t('reset'),
            }),
        });
        try {
            await resetTenant({ id: tenant.data.id });

            await Promise.all([
                queryClient.invalidateQueries({ queryKey: getGetTenantsQueryKey() }),
                queryClient.invalidateQueries({ queryKey: getSearchUsersQueryKey() }),
            ]);

            toastNotifications.success({
                message: t('wasSuccessful', {
                    type: t('tenant'),
                    name: tenant.data.name,
                    action: t('reset'),
                }),
            });
        } catch {
            toastNotifications.error({
                message: t('wasNotSuccessful', {
                    type: t('tenant'),
                    name: tenant.data.name,
                    action: t('reset'),
                }),
            });
        } finally {
            toastNotifications.hide(id);
            setConfirmText('');
        }
    };

    return (
        <>
            {variant === RowActionVariant.DEFAULT ? (
                <Tooltip label={t('resetTenantUsers')}>
                    <ActionIcon onClick={handleResetTenantClick}>
                        <Icon icon="reset_wrench" size="xl" />
                    </ActionIcon>
                </Tooltip>
            ) : (
                <Menu.Item leftSection={<Icon icon="reset_wrench" size="xl" />} onClick={handleResetTenantClick}>
                    {t('resetTenantUsers')}
                </Menu.Item>
            )}
            {open && (
                <ConfirmationModal
                    confirmText={t('reset')}
                    isConfirmationDisabled={confirmText !== tenant.data.name}
                    title={t('resetTenantUsers')}
                    isOpen
                    onCancel={() => setOpen(false)}
                    onConfirm={handleConfirm}
                >
                    <Stack>
                        <Text>
                            {t('resetTenantUsersExplanation', {
                                tenant: tenant.data.name,
                            })}
                        </Text>
                        <Text>{t('typeNameToConfirm', { name: tenant.data.name })}</Text>
                        <TextInput
                            placeholder={tenant.data.name}
                            value={confirmText}
                            autoFocus
                            onChange={(event) => setConfirmText(event.currentTarget.value)}
                        />
                    </Stack>
                </ConfirmationModal>
            )}
        </>
    );
};
